import {
  ATIVO_MERCADO,
  ATIVO_TIPO,
  FUNDO_DISTRIBUICAO,
  PUBLICO_ALVO
} from '@investirapri/financa.js/lib/enums';
import { BENCHMARKS, FUNDO_SITUACAO, SETORES } from '../enums';
import { createHybridSliderScale } from './sliderScales';
import {
  ATIVOS_BASIC_SORT_OPTIONS,
  ATIVOS_FIXED_INCOME_SORT_OPTIONS,
  ATIVOS_FUND_SORT_GROUPS,
  ATIVOS_FUND_SORT_OPTIONS
} from './ativosSortOptions';

export const ATIVOS_FILTER_CONTROL = {
  MULTI_SELECT: 'multiSelect',
  NUMERIC: 'numeric',
  SINGLE_SELECT: 'singleSelect'
};

const wBooleanParam = { type: 'boolean' };
const wStringParam = { type: 'string' };
const wCsvParam = { type: 'csv' };
const wNumberParam = { type: 'number' };
const wNonNegativeNumberParam = { ...wNumberParam, minimum: 0 };
const wNonNegativeIntegerParam = { ...wNonNegativeNumberParam, integer: true };

const wHybridSliderLogPositions = 400;
const wPercentageSliderMaximum = 100;
const wPercentageSliderStep = 0.1;
const wAplicacaoMinSliderMaximum = 10000000;
const wAplicacaoMinSliderLinearMaximum = 100000;
const wAplicacaoMinSliderValueStep = 100;
const wAplicacaoMinSliderScale = createHybridSliderScale({
  maximum: wAplicacaoMinSliderMaximum,
  linearMaximum: wAplicacaoMinSliderLinearMaximum,
  valueStep: wAplicacaoMinSliderValueStep,
  logPositions: wHybridSliderLogPositions
});
const wCurrencySliderMaximum = 100000000000;
const wCurrencySliderLinearMaximum = 1000000000;
const wCurrencySliderValueStep = 1000000;
const wCurrencySliderScale = createHybridSliderScale({
  maximum: wCurrencySliderMaximum,
  linearMaximum: wCurrencySliderLinearMaximum,
  valueStep: wCurrencySliderValueStep,
  logPositions: wHybridSliderLogPositions
});
const wCotistasSliderMaximum = 10000000;
const wCotistasSliderLinearMaximum = 100000;
const wCotistasSliderValueStep = 100;
const wCotistasSliderScale = createHybridSliderScale({
  maximum: wCotistasSliderMaximum,
  linearMaximum: wCotistasSliderLinearMaximum,
  valueStep: wCotistasSliderValueStep,
  logPositions: wHybridSliderLogPositions
});

const wCommonRequestParams = {
  page: { type: 'number' },
  size: { type: 'number' },
  sort: wStringParam,
  cancelado: wStringParam,
  pesquisa: wStringParam
};

const wBooleanOptions = [
  { id: 'todos', label: 'Todos', value: '', neutral: true },
  { id: 'sim', label: 'Sim', value: true },
  { id: 'nao', label: 'Não', value: false }
];

const wOpenClosedOptions = [
  { id: 'todos', label: 'Todos', value: '', neutral: true },
  { id: 'aberto', label: 'Aberto', value: false },
  { id: 'fechado', label: 'Fechado', value: true }
];

const wLegacyIgnoredAtivoTipoIds = [
  ATIVO_TIPO.ACNB.id, // Ação - Bônus de Subscrição
  ATIVO_TIPO.ACNA.id, // Ação - Aluguel - BTC
  ATIVO_TIPO.ACND.id, // Ação - Direito
  ATIVO_TIPO.ACNC.id, // Ação Conversível
  ATIVO_TIPO.ACNR.id, // Ação - Recibo
  ATIVO_TIPO.BTSR.id, // Termo Flexível de BDR
  ATIVO_TIPO.BTUR.id, // Termo em Dólar de BDR
  ATIVO_TIPO.XTBA.id, // Título Bancário
  ATIVO_TIPO.XTPR.id, // Título Privado
  ATIVO_TIPO.XTPU.id, // Título Público
  ATIVO_TIPO.BDRETFCM.id, // BDR Exchange Traded Fund - COMMD
  ATIVO_TIPO.BDRETFRF.id, // BDR Exchange Traded Fund - RF
  ATIVO_TIPO.BDRETFRV.id, // BDR Exchange Traded Fund - RV
  ATIVO_TIPO.CTCB.id, // Clube de Investimento
  ATIVO_TIPO.EMPETF.id, // Empréstimo de ETF
  ATIVO_TIPO.EMPCT.id, // Empréstimo de Fundo
  ATIVO_TIPO.CTUM.id, // Termo em Dólar de Fundo
  ATIVO_TIPO.CTSM.id, // Termo Flexível de Fundo
  ATIVO_TIPO.DC.id, // Direito Creditório
  ATIVO_TIPO.RDB.id, // Recibo de Depósito Bancário
  ATIVO_TIPO.CCCB.id, // Certificado de Cédula de Crédito Bancário
  ATIVO_TIPO.CDCA.id, // Certificado de Direitos Creditórios do Agronegócio
  ATIVO_TIPO.CCR.id, // Cédula de Crédito Rural
  ATIVO_TIPO.DEBI.id, // Debênture Incentivada
  ATIVO_TIPO.DEBP.id, // Debênture Participativa
  ATIVO_TIPO.NCA.id, // Nota Comercial do Agronegócio
  ATIVO_TIPO.NCE.id, // Nota de Crédito à Exportação
  ATIVO_TIPO.TPRT.id, // Título Privado - Termo
  ATIVO_TIPO.BTN.id, // Bônus do Tesouro Nacional
  ATIVO_TIPO.LFTA.id, // Letra Financeira do Tesouro Série A
  ATIVO_TIPO.LFTB.id, // Letra Financeira do Tesouro Série B
  ATIVO_TIPO.NTND.id, // Nota do Tesouro Nacional Série D
  ATIVO_TIPO.NTNE.id, // Nota do Tesouro Nacional Série E
  ATIVO_TIPO.NTNG.id, // Nota do Tesouro Nacional Série G
  ATIVO_TIPO.NTNH.id, // Nota do Tesouro Nacional Série H
  ATIVO_TIPO.NTNJ.id, // Nota do Tesouro Nacional Série J
  ATIVO_TIPO.NTNL.id, // Nota do Tesouro Nacional Série L
  ATIVO_TIPO.NTNM.id, // Nota do Tesouro Nacional Série M
  ATIVO_TIPO.NTNN.id, // Nota do Tesouro Nacional Série N
  ATIVO_TIPO.NTNO.id, // Nota do Tesouro Nacional Série O
  ATIVO_TIPO.NTNR.id, // Nota do Tesouro Nacional Série R
  ATIVO_TIPO.NTNS.id, // Nota do Tesouro Nacional Série S
  ATIVO_TIPO.NTNT.id, // Nota do Tesouro Nacional Série T
  ATIVO_TIPO.NTNU.id, // Nota do Tesouro Nacional Série U
  ATIVO_TIPO.TPUT.id // Título Público - Termo
];

/** Ordena opções de domínio pelo texto apresentado. */
function pvOrderedOptions(pOptions, pKey) {
  const xOptions = Array.isArray(pOptions) ? pOptions : Object.values(pOptions);

  return [...xOptions].sort((pFirst, pSecond) =>
    String(pFirst[pKey] || '').localeCompare(String(pSecond[pKey] || ''), 'pt-BR')
  );
}

/** Monta as opções de tipo correspondentes ao mercado informado. */
function pvGetAtivoTipoOptions(pMercado, pShowSigla = false) {
  return pvOrderedOptions(ATIVO_TIPO, 'descricao')
    .filter(pOption => pOption.mercado === pMercado)
    .filter(pOption => !wLegacyIgnoredAtivoTipoIds.includes(pOption.id))
    .map(pOption => ({
      id: pOption.id || pOption.ativo_tipo_id,
      label: pShowSigla ? `${pOption.sigla} - ${pOption.descricao}` : pOption.descricao,
      value: pOption.id || pOption.ativo_tipo_id
    }));
}

/** Monta as opções disponíveis para seleção múltipla de setores. */
function pvGetSetorOptions() {
  return pvOrderedOptions(SETORES, 'setor').map(pOption => ({
    id: pOption.setor_id,
    label: pOption.setor,
    value: pOption.setor_id
  }));
}

/** Cria o contrato completo de um mercado a partir dos filtros declarados. */
function pvCreateMercadoConfig(pMercado, pFields, pSortOptions, pSortGroups = null) {
  const xQueryParams = pFields.reduce(
    (pParams, pField) => ({
      ...pParams,
      [pField.name]: pField.query
    }),
    {
      pesquisa: wStringParam,
      sort: {
        type: 'enum',
        values: pSortOptions.map(pOption => pOption.value).filter(Boolean)
      }
    }
  );

  return {
    mercado: pMercado,
    fields: pFields,
    sortOptions: pSortOptions,
    sortGroups: pSortGroups,
    queryParams: xQueryParams,
    requestParams: {
      ...wCommonRequestParams,
      ...xQueryParams
    }
  };
}

/** Cria os filtros iniciais compartilhados pelos mercados não FND. */
function pvCreateBasicMercadoFields(pMercado) {
  return [
    {
      name: 'ativo_tipo_id',
      label: 'Tipo de ativo',
      control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
      options: pvGetAtivoTipoOptions(pMercado),
      query: wCsvParam
    },
    {
      name: 'top_setor_id',
      label: 'Setor',
      control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
      options: pvGetSetorOptions(),
      query: wCsvParam
    }
  ];
}

const wFundFields = [
  {
    name: 'ativo_tipo_id',
    label: 'Tipo de ativo',
    control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
    options: pvGetAtivoTipoOptions(ATIVO_MERCADO.FND.id, true),
    query: wCsvParam
  },
  {
    name: 'benchmark_indicador_id',
    label: 'Benchmark',
    control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
    options: pvOrderedOptions(BENCHMARKS, 'index').map(pOption => ({
      id: pOption.indicador_id,
      label: pOption.indicador_sigla,
      value: pOption.indicador_id
    })),
    query: wCsvParam
  },
  {
    name: 'situacao',
    label: 'Situação',
    control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
    options: pvOrderedOptions(FUNDO_SITUACAO, 'descricao')
      .filter(pOption => pOption.value !== FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value)
      .map(pOption => ({
        id: pOption.id,
        label: pOption.descricao,
        value: pOption.value
      })),
    query: wCsvParam
  },
  {
    name: 'publico_alvo',
    label: 'Público-alvo',
    control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
    options: pvOrderedOptions(PUBLICO_ALVO, 'descricao').map(pOption => ({
      id: pOption.id,
      label: pOption.descricao,
      value: pOption.id
    })),
    query: wCsvParam
  },
  {
    name: 'distribuicao',
    label: 'Distribuição',
    control: ATIVOS_FILTER_CONTROL.MULTI_SELECT,
    options: pvOrderedOptions(FUNDO_DISTRIBUICAO, 'descricao').map(pOption => ({
      id: pOption.id,
      label: pOption.descricao,
      value: pOption.id
    })),
    query: wCsvParam
  },
  ...[
    // Reavaliar futuramente se a adaptação à Resolução CVM 175 deve voltar à interface.
    // { name: 'cvm_175', label: 'Adaptação à Resolução CVM 175' },
    { name: 'exclusivo', label: 'Exclusivo' },
    { name: 'fechado', label: 'Fundo aberto/fechado', options: wOpenClosedOptions },
    { name: 'longo', label: 'Longo prazo' },
    { name: 'cotas', label: 'Fundo de cotas' },
    {
      name: 'condominio_fechado',
      label: 'Condomínio aberto/fechado',
      options: wOpenClosedOptions
    },
    { name: 'responsabilidade_limitada', label: 'Responsabilidade limitada' },
    { name: 'qualificado', label: 'Qualificado' },
    { name: 'profissional', label: 'Profissional' },
    { name: 'previdenciario', label: 'Previdenciário' },
    { name: 'estruturado', label: 'Estruturado' },
    { name: 'esg', label: 'ESG' }
  ].map(pField => ({
    ...pField,
    control: ATIVOS_FILTER_CONTROL.SINGLE_SELECT,
    options: pField.options || wBooleanOptions,
    query: wBooleanParam
  })),
  {
    name: 'taxa_adm',
    label: 'Taxa de administração',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'max',
    minimum: 0,
    maximum: wPercentageSliderMaximum,
    step: wPercentageSliderStep,
    showSlider: true,
    suffix: '%',
    query: wNonNegativeNumberParam
  },
  {
    name: 'aplicacao_min',
    label: 'Aplicação mínima',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wAplicacaoMinSliderMaximum,
    step: wAplicacaoMinSliderValueStep,
    showSlider: true,
    sliderScale: wAplicacaoMinSliderScale,
    currency: 'BRL',
    query: wNonNegativeNumberParam
  },
  {
    name: 'resgate_carencia',
    label: 'Prazo para resgate',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'max',
    decimalScale: 0,
    minimum: 0,
    maximum: 365,
    step: 1,
    showSlider: true,
    suffix: ' dias',
    query: wNonNegativeIntegerParam
  },
  {
    name: 'retorno',
    label: 'Retorno',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    allowNegative: true,
    suffix: '%',
    query: wNumberParam
  },
  {
    name: 'perc_dias_acima_benchmark',
    label: 'Dias acima do benchmark',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wPercentageSliderMaximum,
    step: wPercentageSliderStep,
    showSlider: true,
    suffix: '%',
    query: wNonNegativeNumberParam
  },
  {
    name: 'perc_meses_acima_benchmark',
    label: 'Meses acima do benchmark',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wPercentageSliderMaximum,
    step: wPercentageSliderStep,
    showSlider: true,
    suffix: '%',
    query: wNonNegativeNumberParam
  },
  {
    name: 'sharpe',
    label: 'Sharpe',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    allowNegative: true,
    query: wNumberParam
  },
  {
    name: 'tracking_error',
    label: 'Tracking error',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wPercentageSliderMaximum,
    step: wPercentageSliderStep,
    showSlider: true,
    suffix: '%',
    query: wNonNegativeNumberParam
  },
  {
    name: 'volatilidade',
    label: 'Volatilidade',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'max',
    minimum: 0,
    maximum: wPercentageSliderMaximum,
    step: wPercentageSliderStep,
    showSlider: true,
    suffix: '%',
    query: wNonNegativeNumberParam
  },
  {
    name: 'pl',
    label: 'Patrimônio líquido',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wCurrencySliderMaximum,
    step: wCurrencySliderValueStep,
    showSlider: true,
    sliderScale: wCurrencySliderScale,
    currency: 'BRL',
    query: wNonNegativeNumberParam
  },
  {
    name: 'cotistas',
    label: 'Cotistas',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    decimalScale: 0,
    minimum: 0,
    maximum: wCotistasSliderMaximum,
    step: wCotistasSliderValueStep,
    showSlider: true,
    sliderScale: wCotistasSliderScale,
    query: wNonNegativeIntegerParam
  },
  {
    name: 'gestor_pl',
    label: 'Patrimônio líquido',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wCurrencySliderMaximum,
    step: wCurrencySliderValueStep,
    showSlider: true,
    sliderScale: wCurrencySliderScale,
    currency: 'BRL',
    query: wNonNegativeNumberParam
  },
  {
    name: 'gestor_cotistas',
    label: 'Cotistas',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    decimalScale: 0,
    minimum: 0,
    maximum: wCotistasSliderMaximum,
    step: wCotistasSliderValueStep,
    showSlider: true,
    sliderScale: wCotistasSliderScale,
    query: wNonNegativeIntegerParam
  },
  {
    name: 'adm_pl',
    label: 'Patrimônio líquido',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    minimum: 0,
    maximum: wCurrencySliderMaximum,
    step: wCurrencySliderValueStep,
    showSlider: true,
    sliderScale: wCurrencySliderScale,
    currency: 'BRL',
    query: wNonNegativeNumberParam
  },
  {
    name: 'adm_cotistas',
    label: 'Cotistas',
    control: ATIVOS_FILTER_CONTROL.NUMERIC,
    direction: 'min',
    decimalScale: 0,
    minimum: 0,
    maximum: wCotistasSliderMaximum,
    step: wCotistasSliderValueStep,
    showSlider: true,
    sliderScale: wCotistasSliderScale,
    query: wNonNegativeIntegerParam
  }
];

const wAtivosFilterRegistry = {
  [ATIVO_MERCADO.FND.id]: pvCreateMercadoConfig(
    ATIVO_MERCADO.FND.id,
    wFundFields,
    ATIVOS_FUND_SORT_OPTIONS,
    ATIVOS_FUND_SORT_GROUPS
  ),
  [ATIVO_MERCADO.TPU.id]: pvCreateMercadoConfig(
    ATIVO_MERCADO.TPU.id,
    pvCreateBasicMercadoFields(ATIVO_MERCADO.TPU.id),
    ATIVOS_FIXED_INCOME_SORT_OPTIONS
  ),
  [ATIVO_MERCADO.TPR.id]: pvCreateMercadoConfig(
    ATIVO_MERCADO.TPR.id,
    pvCreateBasicMercadoFields(ATIVO_MERCADO.TPR.id),
    ATIVOS_FIXED_INCOME_SORT_OPTIONS
  ),
  [ATIVO_MERCADO.TBA.id]: pvCreateMercadoConfig(
    ATIVO_MERCADO.TBA.id,
    pvCreateBasicMercadoFields(ATIVO_MERCADO.TBA.id),
    ATIVOS_FIXED_INCOME_SORT_OPTIONS
  ),
  [ATIVO_MERCADO.ACN.id]: pvCreateMercadoConfig(
    ATIVO_MERCADO.ACN.id,
    pvCreateBasicMercadoFields(ATIVO_MERCADO.ACN.id),
    ATIVOS_BASIC_SORT_OPTIONS
  )
};

/** Retorna a configuração completa de filtros registrada para o mercado. */
export function getAtivosFilterConfig(pMercado) {
  return wAtivosFilterRegistry[pMercado] || null;
}

export default { ATIVOS_FILTER_CONTROL, getAtivosFilterConfig };
